import { useEffect, useState } from 'react';
import { Lock, Mail } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LegalFooter } from '../components/layout';
import { TextField } from '../components/forms';
import { Btn, Logo } from '../components/ui';
import { TurnstileWidget } from '../components/forms/TurnstileWidget';
import { authApi, INVALID_RESET_TOKEN_MESSAGE } from '../api/authApi';
import { ApiError } from '../api/errors';
import { c } from '../styles/theme';
import { paths } from './paths';

const ICON_SIZE = 14;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const firstAccess = searchParams.get('flow') === 'access';
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetting = token.trim().length > 0;

  useEffect(() => {
    if (!resetting) {
      setTokenChecked(false);
      setTokenValid(false);
      return;
    }
    let active = true;
    setPending(true);
    setError('');
    setNotice('');
    void authApi
      .validatePasswordResetToken(token)
      .then(() => {
        if (!active) return;
        setTokenValid(true);
        setTokenChecked(true);
      })
      .catch((err) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : INVALID_RESET_TOKEN_MESSAGE;
        setTokenValid(false);
        setTokenChecked(true);
        setError(message);
      })
      .finally(() => {
        if (!active) return;
        setPending(false);
      });

    return () => {
      active = false;
    };
  }, [resetting, token]);

  async function handleRequest() {
    setError('');
    setNotice('');
    setPending(true);
    try {
      await authApi.requestPasswordReset(email, turnstileToken);
      setNotice('If an account exists for that email, we’ve sent a password reset link.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not send reset link';
      if (err instanceof ApiError && err.code === 'oauth_passwordless_account') {
        setNotice(message);
      } else {
        setError(message);
      }
    } finally {
      setPending(false);
      setTurnstileToken('');
      setTurnstileResetKey((value) => value + 1);
    }
  }

  async function handleConfirm() {
    setError('');
    setPending(true);
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      setPending(false);
      return;
    }
    try {
      await authApi.confirmPasswordReset(token, newPassword);
      navigate(paths.login, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      style={{
        fontFamily: 'Inter, sans-serif',
        background: c.bg,
        color: c.text1,
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Logo size={30} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void (resetting ? handleConfirm() : handleRequest());
          }}
          style={{
            background: c.surface1,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: '24px 22px',
          }}
        >
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 18,
              margin: '0 0 4px',
              textAlign: 'center',
              color: c.text1,
            }}
          >
            {resetting
              ? firstAccess
                ? 'Create your password'
                : 'Reset your password'
              : 'Forgot password?'}
          </h1>
          <p style={{ fontSize: 12, color: c.text3, margin: '0 0 20px', textAlign: 'center' }}>
            {resetting
              ? tokenChecked && !tokenValid
                ? 'This reset link has expired or is no longer valid. Request a fresh link to choose a new password.'
                : firstAccess
                  ? 'Choose a password to verify your email and access your Lyrixsa AI account'
                  : 'Choose a new password for your Lyrixsa AI account'
              : 'Enter your email and we’ll send a reset link when this account uses a password.'}
          </p>

          {resetting && tokenChecked && !tokenValid ? null : resetting ? (
            <>
              <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="At least 10 characters"
                icon={<Lock size={ICON_SIZE} color={c.text3} />}
                marginBottom={12}
              />
              <TextField
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter your password"
                icon={<Lock size={ICON_SIZE} color={c.text3} />}
                marginBottom={6}
              />
            </>
          ) : (
            <>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                icon={<Mail size={ICON_SIZE} color={c.text3} />}
                marginBottom={6}
              />
              <TurnstileWidget
                action="password_reset"
                resetKey={turnstileResetKey}
                onTokenChange={setTurnstileToken}
              />
            </>
          )}

          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '6px 0 0' }}>
              {error}
            </p>
          )}
          {!error && notice && (
            <p role="status" style={{ fontSize: 12, color: c.text2, margin: '6px 0 0' }}>
              {notice}
            </p>
          )}

          <Btn
            full
            type="submit"
            disabled={
              pending ||
              (resetting && tokenChecked && !tokenValid) ||
              (!resetting && !turnstileToken)
            }
            style={{ marginTop: 14, marginBottom: 10 }}
          >
            {resetting
              ? pending && !tokenChecked
                ? 'Checking link…'
                : pending
                  ? 'Resetting…'
                  : tokenChecked && !tokenValid
                    ? 'Link expired'
                    : firstAccess
                      ? 'Create password'
                      : 'Reset password'
              : pending
                ? 'Sending…'
                : 'Send reset link'}
          </Btn>

          <button
            type="button"
            onClick={() => navigate(paths.login)}
            style={{
              background: 'none',
              border: 'none',
              color: c.accent,
              cursor: 'pointer',
              fontSize: 12,
              padding: 0,
              fontWeight: 500,
              width: '100%',
            }}
          >
            Back to login
          </button>
        </form>

        <LegalFooter marginTop={20} centered />
      </div>
    </div>
  );
}
